import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Alert,
  Box,
  Typography,
} from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import { CreateUploadData, Partner } from '@/services/partnerService';

interface UploadDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  onUpload: (data: CreateUploadData) => Promise<void>;
  partners: Partner[];
  loading?: boolean;
}

export const UploadDocumentDialog: React.FC<UploadDocumentDialogProps> = ({
  open,
  onClose,
  onUpload,
  partners,
  loading = false,
}) => {
  const [formData, setFormData] = useState<CreateUploadData>({
    partnerId: '',
    type: 'Inspection',
    document: '',
    documentUrl: '',
    vehicleId: '',
    notes: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [uploadError, setUploadError] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  useEffect(() => {
    if (open) {
      setFormData({
        partnerId: '',
        type: 'Inspection',
        document: '',
        documentUrl: '',
        vehicleId: '',
        notes: '',
      });
      setErrors({});
      setUploadError('');
      setSelectedFile(null);
    }
  }, [open]);

  const handleChange = (field: keyof CreateUploadData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setFormData(prev => ({
        ...prev,
        document: file.name,
      }));
      if (errors.document) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.document;
          return newErrors;
        });
      }
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.partnerId) {
      newErrors.partnerId = 'Please select a partner';
    }

    if (!formData.document) {
      newErrors.document = 'Please select a document to upload';
    }

    if (!formData.vehicleId.trim()) {
      newErrors.vehicleId = 'Vehicle ID is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpload = async () => {
    if (!validate()) return;

    setUploadError('');

    try {
      // In a real implementation, you would upload the file first
      // and get back a URL to store
      const uploadData = {
        ...formData,
        documentUrl: selectedFile ? URL.createObjectURL(selectedFile) : '',
      };

      await onUpload(uploadData);
      onClose();
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'Failed to upload document');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Upload Document
      </DialogTitle>

      <DialogContent>
        {uploadError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {uploadError}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required error={!!errors.partnerId}>
              <InputLabel>Partner</InputLabel>
              <Select
                value={formData.partnerId}
                label="Partner"
                onChange={(e) => handleChange('partnerId', e.target.value)}
              >
                {partners.map((partner) => (
                  <MenuItem key={partner.id} value={partner.id}>
                    {partner.name} ({partner.serviceType})
                  </MenuItem>
                ))}
              </Select>
              {errors.partnerId && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                  {errors.partnerId}
                </Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Document Type</InputLabel>
              <Select
                value={formData.type}
                label="Document Type"
                onChange={(e) => handleChange('type', e.target.value)}
              >
                <MenuItem value="Inspection">Inspection Report</MenuItem>
                <MenuItem value="GPS">GPS Installation</MenuItem>
                <MenuItem value="Documentation">Documentation</MenuItem>
                <MenuItem value="Insurance">Insurance Verification</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Vehicle ID"
              value={formData.vehicleId}
              onChange={(e) => handleChange('vehicleId', e.target.value)}
              error={!!errors.vehicleId}
              helperText={errors.vehicleId}
              required
              placeholder="e.g., VIN or Registration Number"
            />
          </Grid>

          <Grid item xs={12}>
            <Box
              sx={{
                border: '2px dashed',
                borderColor: errors.document ? 'error.main' : 'divider',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                backgroundColor: 'grey.50',
              }}
            >
              <input
                accept="application/pdf,image/*"
                style={{ display: 'none' }}
                id="upload-file"
                type="file"
                onChange={handleFileSelect}
              />
              <label htmlFor="upload-file">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUpload />}
                  size="large"
                >
                  Select Document
                </Button>
              </label>

              {selectedFile && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Selected: <strong>{selectedFile.name}</strong>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Size: {(selectedFile.size / 1024).toFixed(2)} KB
                  </Typography>
                </Box>
              )}

              {!selectedFile && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  PDF or image files (max 10MB)
                </Typography>
              )}

              {errors.document && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                  {errors.document}
                </Typography>
              )}
            </Box>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes (Optional)"
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              multiline
              rows={3}
              placeholder="Add any additional notes about this upload..."
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <CloudUpload />}
        >
          {loading ? 'Uploading...' : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
